// Deterministic hardware / LLM compatibility estimation.

export function weightsGB(params, bits) {
  return (params * bits) / 8;
}

// Weights + runtime & KV-cache overhead.
export function requiredGB(params, bits) {
  return weightsGB(params, bits) * 1.1 + 0.4;
}

const VERDICT_META = {
  can_run: { label: "OPTIMAL", tone: "emerald", desc: "Tient dans le matériel, accélération complète" },
  tight: { label: "SERRÉ", tone: "amber", desc: "Fonctionne mais charge élevée / plus lent" },
  no_go: { label: "INSUFFISANT", tone: "red", desc: "Mémoire insuffisante pour ce modèle" },
};

export function verdictMeta(v) {
  return VERDICT_META[v] || VERDICT_META.no_go;
}

function gpuProfile(hw) {
  const unified = hw.accelerator === "metal";
  const noGpu = hw.accelerator === "cpu";
  const gpus = unified || noGpu ? 1 : Math.max(1, hw.gpuCount || 1);
  const mem = unified ? hw.ram : noGpu ? 0 : (hw.vram || 0) * gpus;
  const label = unified ? "GPU (mémoire unifiée)" : gpus > 1 ? `${gpus} GPU (répartition)` : "GPU";
  const split = gpus > 1 ? 0.85 : 1;
  return { mem, label, fast: (unified ? 260 : 720) * split, slow: (unified ? 150 : 360) * split };
}

export function evaluate(model, quant, hw) {
  const req = requiredGB(model.params, quant.bits);
  const gpu = gpuProfile(hw);
  const ram = hw.ram || 0;
  const result = (verdict, target, mode, tokPerSec) => ({ requiredGB: req, verdict, tokPerSec, target, mode });
  if (gpu.mem > 0 && req <= gpu.mem * 0.9) return result("can_run", gpu.mem, gpu.label, Math.max(10, Math.round(gpu.fast / model.params)));
  if (gpu.mem > 0 && req <= gpu.mem) return result("tight", gpu.mem, gpu.label, Math.max(4, Math.round(gpu.slow / model.params)));
  if (req <= ram * 0.9) return result("tight", ram, "CPU / offload partiel", Math.max(1, Math.round(90 / model.params)));
  return result("no_go", Math.max(gpu.mem, ram), "—", 0);
}

// Best quant that still runs optimally; falls back to smallest quant otherwise.
export function recommendedQuant(model, hw, quants) {
  const ascending = [...quants].sort((a, b) => a.bits - b.bits);
  let optimal = null;
  for (const q of ascending) {
    if (evaluate(model, q, hw).verdict === "can_run") optimal = q;
  }
  if (optimal) return optimal;
  for (const q of ascending) {
    if (evaluate(model, q, hw).verdict === "tight") return q;
  }
  return ascending[0];
}

export function bestVerdict(model, hw, quants) {
  const ascending = [...quants].sort((a, b) => a.bits - b.bits);
  const verdicts = ascending.map((q) => evaluate(model, q, hw).verdict);
  if (verdicts.includes("can_run")) return "can_run";
  if (verdicts.includes("tight")) return "tight";
  return "no_go";
}

function acceleratorFromRenderer(name) {
  const r = name.toLowerCase();
  if (r.includes("apple") || r.includes("metal")) return "metal";
  if (r.includes("nvidia") || r.includes("geforce") || r.includes("rtx")) return "cuda";
  if (r.includes("amd") || r.includes("radeon")) return "rocm";
  return undefined;
}

function detectGpuRenderer() {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    const dbg = gl?.getExtension("WEBGL_debug_renderer_info");
    const renderer = dbg && gl.getParameter(dbg.UNMASKED_RENDERER_GL);
    return renderer ? String(renderer).replace(/ANGLE \(|\)/g, "").trim() : null;
  } catch (e) {
    console.warn("Détection GPU indisponible", e);
    return null;
  }
}

export async function detectHardware() {
  const out = {};
  if (navigator.hardwareConcurrency) out.cpuCores = navigator.hardwareConcurrency;
  if (navigator.deviceMemory) out.ram = navigator.deviceMemory;
  const gpu = detectGpuRenderer();
  if (gpu) {
    out.gpu = gpu;
    const acc = acceleratorFromRenderer(gpu);
    if (acc) out.accelerator = acc;
  }
  return out;
}
